import { VerificationStatus } from "../utils/getStatus";
import { TDocument } from "./document";

export * from "./document";

export type TVerificationRequestUserSummary = {
  first_name?: string;
  last_name?: string;
  [key: string]: unknown;
};

export type TVerificationRequestEntitySummary = {
  entity_name?: string;
  [key: string]: unknown;
};

export type TVerificationRequestAdditionalItems = {
  user_information?: string;
  userInformationParsed?: TVerificationRequestUserSummary;
  entity_information?: string;
  entityInformationParsed?: TVerificationRequestEntitySummary;
};

export type TVerificationRequest = {
  category: string;
  deletedAt: string;
  companyId: string;
  appId: number;
  appName?: string;
  identificationStatus: string;
  screeningStatus: string;
  id: string;
  status: VerificationStatus;
  statusReason: string;
  riskLevel: string;
  riskLevelReason: string;
  declineReason: [];
  createdAt: string;
  updatedAt: string;
  decisionTime: string;
  appPublicKey: string;
  additionalItems?: TVerificationRequestAdditionalItems;
  email?: string;
  documents: TDocument[];
};

export type TVerificationRequestAccessMode = "denied" | "readonly" | "update";

export type TVerificationRequestAccess = {
  mode: TVerificationRequestAccessMode;
};

export type TComplianceComment = {
  verificationId: "string";
  userDocumentId: "string";
  comment: "string";
  createdAt: "2024-12-02T09:12:49.993Z";
  updatedAt: "2024-12-02T09:12:49.993Z";
};

export type TScreeningEvent = {
  id: number;
  type: string;
  description: string;
  ackedBy: string;
  createdAt: string;
};

export type TExternalProviderID = {
  provider: string;
  kind: string;
  value: string;
};

export type TLinkedVerificationRequest = {
  verificationRequestId: string;
  linkType: string;
  status: string;
  category: string;
  name: string;
};

export type TLinkedVerificationRequestResponse = TLinkedVerificationRequest;

export enum HistoryType {
  HISTORY_TYPE_UNSPECIFIED = "HISTORY_TYPE_UNSPECIFIED",
  HISTORY_TYPE_CREATED = "HISTORY_TYPE_CREATED",
  HISTORY_TYPE_DELETED = "HISTORY_TYPE_DELETED",
  HISTORY_TYPE_STATUS_CHANGED = "HISTORY_TYPE_STATUS_CHANGED",
  HISTORY_TYPE_RISK_LEVEL_CHANGED = "HISTORY_TYPE_RISK_LEVEL_CHANGED",
  HISTORY_TYPE_INFORMATION_REQUESTED = "HISTORY_TYPE_INFORMATION_REQUESTED",
  HISTORY_TYPE_STATUS_REASON_CHANGED = "HISTORY_TYPE_STATUS_REASON_CHANGED",
  HISTORY_TYPE_DOCUMENT_CHANGED = "HISTORY_TYPE_DOCUMENT_CHANGED",
}

export type THistStatusChange = {
  fromStatus?: string;
  toStatus?: string;
  reason?: string;
  message?: string;
};

export type THistRiskLevelChange = {
  fromRiskLevel?: string;
  toRiskLevel?: string;
  reason?: string;
};

export type THistInformationRequest = {
  message?: string;
};

export type THistStatusReasonChange = {
  fromStatusReason?: string;
  toStatusReason?: string;
};

export type THistDocumentRowOperation = "create" | "delete" | "change";

export type THistDocumentFieldType =
  | "text"
  | "url"
  | "select"
  | "countrySelect"
  | "date"
  | "filePicker"
  | "taxId"
  | "sourceOfActive"
  | "checkbox"
  | "pep";

export type THistDocumentValueChange =
  | {
      operation: "change";
      before: unknown;
      after: unknown;
    }
  | {
      operation: "add";
      after: unknown;
      before?: never;
    }
  | {
      operation: "delete";
      before: unknown;
      after?: never;
    };

export type THistDocumentRecordChange = {
  operation: THistDocumentRowOperation;
  fields?: THistDocumentFieldChange[];
};

export type THistDocumentFieldChange = {
  name: string;
  label?: string;
  type?: THistDocumentFieldType | "";
  multiple?: boolean;
  changes?: THistDocumentValueChange[];
};

export type THistDocumentDataChange = {
  operation: THistDocumentRowOperation;
  dataId: string;
  documentDataId: string;
  fields?: THistDocumentFieldChange[];
};

export type THistDocumentFileValue = {
  originalName: string;
  mimeType: string;
  size: string;
  kind?: string;
  downloadRef?: string;
};

export type THistDocumentFileMetaChange = {
  name: string;
  before: unknown;
  after: unknown;
};

export type THistDocumentFileChangeBase = {
  dataId: string;
  documentDataId: string;
  fieldName?: string;
  fieldLabel?: string;
};

export type THistDocumentFileUploadChange = THistDocumentFileChangeBase & {
  operation: "upload";
  after: THistDocumentFileValue;
  before?: never;
  meta?: never;
};

export type THistDocumentFileDeleteChange = THistDocumentFileChangeBase & {
  operation: "delete";
  before: THistDocumentFileValue;
  after?: never;
  meta?: never;
};

export type THistDocumentFileReplaceChange = THistDocumentFileChangeBase & {
  operation: "replace";
  before: THistDocumentFileValue;
  after: THistDocumentFileValue;
  meta?: never;
};

export type THistDocumentFileMetadataUpdateChange =
  THistDocumentFileChangeBase & {
    operation: "metadata_update";
    meta: THistDocumentFileMetaChange[];
    before?: never;
    after?: never;
  };

export type THistDocumentFileChange =
  | THistDocumentFileUploadChange
  | THistDocumentFileDeleteChange
  | THistDocumentFileReplaceChange
  | THistDocumentFileMetadataUpdateChange;

export type THistoryDocumentFileChange = THistDocumentFileChange & {
  sourceHistoryId: string;
};

export type THistDocumentChange = {
  documentId: string;
  documentType: string;
  documentName: string;
  document?: THistDocumentRecordChange[];
  data?: THistDocumentDataChange[];
  file?: THistDocumentFileChange[];
};

export type THistDetails = {
  statusChange?: THistStatusChange;
  riskLevelChange?: THistRiskLevelChange;
  informationRequest?: THistInformationRequest;
  informationRequested?: THistInformationRequest;
  statusReasonChange?: THistStatusReasonChange;
  documentChange?: THistDocumentChange;
};

export type MinimalUserInfo = {
  display: string;
};

export type THistory = {
  id: string;
  userId: string;
  userInfo?: MinimalUserInfo;
  createdAt: string;
  type: HistoryType;
  details?: THistDetails;
};

export type THistoryResponse = {
  history: THistory[];
  hasMore: boolean;
};

export const HISTORY_DOCUMENT_CHANGE_GROUP_TYPE = "document_change_group";

export type THistoryDocumentChangeBlock = {
  documentId: string;
  documentType: string;
  documentName: string;
  sourceItemIds: string[];
  document: THistDocumentRecordChange[];
  data: THistDocumentDataChange[];
  file: THistoryDocumentFileChange[];
};

export type THistoryDocumentChangeGroup = {
  type: typeof HISTORY_DOCUMENT_CHANGE_GROUP_TYPE;
  id: string;
  userId: string;
  userInfo?: MinimalUserInfo;
  createdAt: string;
  items: THistory[];
  blocks: THistoryDocumentChangeBlock[];
};

export type THistoryDisplayItem = THistory | THistoryDocumentChangeGroup;

export type THistoryDisplayResponse = {
  history: THistoryDisplayItem[];
  hasMore: boolean;
};
